import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { HighlightRegion, NO_HIGHLIGHT_REGIONS, cleanHighlightLabel } from '../../../../../shared/highlight-regions';
import { LABEL_PADDING_X_PX, LabelRect, estimateLabelWidth, placeLabels } from './label-placement';
import { DEFAULT_FEATHER_PERCENT, resolveFeatherPx, toFiniteDimension } from './spotlight-feather';

/** Height of a label, in screen pixels. Labels keep their size at every zoom level. */
const LABEL_HEIGHT_PX = 26;
/** Distance between a region's outline and its label. */
const LABEL_GAP_PX = 8;
/** Smallest distance between a label and the edge of the drawing. */
const LABEL_MARGIN_PX = 6;
/** How far outside its region an outline is drawn, so the line does not cover the content. */
const OUTLINE_OUTSET_PX = 3;
/**
 * How long a window that is no longer highlighted takes to fade out after a selection change
 * (Issue #3127). It must match the transition in the stylesheet; the window is removed afterwards.
 */
export const SPOTLIGHT_FADE_OUT_MS = 300;
/** How long a newly highlighted window takes to fade in; it must match the stylesheet animation. */
export const SPOTLIGHT_FADE_IN_MS = 450;

/**
 * Spotlight overlay component for highlighting objects on architectural plans.
 *
 * Creates a gray overlay with a transparent "spotlight" window over the highlighted object.
 * The soft edge ("feather") is proportional to the on-screen size of the highlighted box
 * rather than a fixed number of screen pixels, so the halo looks the same relative to the
 * object at every zoom level (Issue #1496).
 *
 * Several regions can be highlighted at once (Issue #2254). Each region gets its own window in
 * the overlay, an outline, and an optional label placed beside it. One region with no label is
 * drawn exactly as the single highlight always was: a window with a soft edge and nothing else.
 *
 * Related: Issue #292 - Spotlight Highlight UI
 * Related: Issue #1496 - Feather scales with bounding-box size, not zoom level
 * Related: Issue #2254 - Several highlighted regions, with labels
 * Parent: Issue #291 - Image Object Search
 */
@Component({
  selector: 'app-spotlight-overlay',
  imports: [],
  templateUrl: './spotlight-overlay.component.html',
  styleUrls: ['./spotlight-overlay.component.scss'],
})
export class SpotlightOverlayComponent implements OnChanges, AfterViewChecked, OnDestroy {
  @ViewChild('spotlightSvg') private spotlightSvg?: ElementRef<SVGSVGElement>;
  @Input() dismissOnEscape = true;

  /**
   * The highlight as one box. With several regions this is the box that contains them all; the
   * overlay is shown while it is set, and Escape dismisses it.
   */
  @Input() highlightBox: BoundingBox | null = null;
  /**
   * The individual regions inside `highlightBox`, each with an optional label (Issue #2254).
   * When empty, `highlightBox` itself is the one region.
   */
  @Input() highlightRegions: readonly HighlightRegion[] = NO_HIGHLIGHT_REGIONS;
  /**
   * A region drawn half shaded beside the highlight: the selected region while the pointer rests on
   * another row of the table of contents (Issue #3127). Null when there is none.
   */
  @Input() dimmedBox: BoundingBox | null = null;
  /**
   * Whether the next change of regions cross-fades: windows that are no longer highlighted fade out
   * and new ones fade in. A selection change fades; a hover changes the highlight at once
   * (Issue #3127).
   */
  @Input() fadeChanges = false;
  @Input() wrapperWidth: number = 0; // Full wrapper width (for SVG sizing)
  @Input() wrapperHeight: number = 0; // Full wrapper height (for SVG sizing)
  @Input() canvasWidth: number = 0; // Canvas display width
  @Input() canvasHeight: number = 0; // Canvas display height
  @Input() canvasScale: number = 1; // Display scale (clientWidth / width)
  @Input() canvasOffsetX: number = 0; // Canvas X position in wrapper (for centering)
  @Input() canvasOffsetY: number = 0; // Canvas Y position in wrapper
  /**
   * Feather size as a percentage of the on-screen box size (width + height).
   * 0 means "no feather" (hard edge); any positive value resolves to a pixel
   * width clamped to [MIN_FEATHER_PX, MAX_FEATHER_PX].
   */
  @Input() featherPercent: number = DEFAULT_FEATHER_PERCENT;

  @Input() overlayOpacity: number = 0.7; // 70% opacity

  @Output() dismiss = new EventEmitter<void>();

  // SVG mask ID (unique per component instance)
  maskId: string;

  /** DOM id of the text list of regions, which the overlay names as its description. */
  regionListId: string;

  // Calculated box for spotlight (in canvas display space)
  scaledBox: BoundingBox = { x: 0, y: 0, width: 0, height: 0 };

  /**
   * Feather width in screen pixels, derived from `featherPercent` and `scaledBox`.
   * The template uses it for both the mask cutout expansion and the blur stdDeviation.
   */
  effectiveGradientWidth: number = 0;

  /** One window per highlighted region, in canvas display space (Issue #2254). */
  cutouts: SpotlightCutout[] = [];

  /** Windows that are fading out after a selection change, with their removal timers (Issue #3127). */
  private leaving = new Map<string, { box: BoundingBox; timer: ReturnType<typeof setTimeout> }>();
  /** Windows still fading in. The class stays on until the animation ends, across re-measures. */
  private entering = new Map<string, ReturnType<typeof setTimeout>>();

  /** The placed labels of the regions that have one. */
  labels: SpotlightLabel[] = [];

  /**
   * True when the regions are outlined: there are several, or one carries a label. A single
   * unlabelled region keeps the plain look it always had.
   */
  annotated = false;

  /** What the highlight is, for assistive technology. */
  ariaLabel = 'Object highlight';

  /** One line of text per region, read by screen readers in place of the drawing. */
  regionDescriptions: string[] = [];

  /** How far outside its region an outline is drawn (used by the template). */
  readonly outlineOutset = OUTLINE_OUTSET_PX;

  /** Horizontal padding inside a label (used by the template). */
  readonly labelPaddingX = LABEL_PADDING_X_PX;

  // ── Live gesture transform mirrored from the preview canvas (Issue #1489) ──
  // During a pinch/trackpad zoom the canvas is scaled with a GPU-composited CSS
  // transform and never repaints, so the stencil's input-driven geometry is
  // stale for the whole gesture. PreviewComponent pushes the canvas's transform
  // here every frame so the stencil tracks the zoom instead of snapping at the
  // end of the gesture. Values are written straight to the DOM (no bindings) to
  // keep the gesture off Angular's change-detection path.
  private gestureTransform = '';
  private gestureTransformOrigin = '';
  private gestureTransition = '';
  private gestureTargetEl: SVGSVGElement | null = null;
  /** True while a camera flight draws the shading on its own canvas (Issue #3127). */
  private flightMode = false;

  constructor() {
    // Generate unique ID for SVG elements
    const uniqueId = Math.random().toString(36).substring(7);
    this.maskId = `spotlight-${uniqueId}`;
    this.regionListId = `${this.maskId}-regions`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Recalculate whenever any dimension, scale, or feather setting changes
    if (
      changes['highlightBox'] ||
      changes['highlightRegions'] ||
      changes['dimmedBox'] ||
      changes['canvasScale'] ||
      changes['canvasWidth'] ||
      changes['canvasHeight'] ||
      changes['featherPercent']
    ) {
      this.calculateScaledBox();
    }
  }

  ngAfterViewChecked(): void {
    // The stencil SVG is created/destroyed by the `@if (highlightBox)` block, so
    // a highlight that appears mid-gesture gets a brand new element with no
    // transform. Re-apply whenever the element identity changes.
    //
    // Normalise undefined -> null before comparing: with no highlight rendered
    // the ViewChild is `undefined` while gestureTargetEl is `null`, and the
    // resulting `undefined !== null` would re-run this on every change-detection
    // cycle for the entire time the overlay is inactive.
    const currentSvg = this.spotlightSvg?.nativeElement ?? null;
    if (currentSvg !== this.gestureTargetEl) {
      this.applyGestureTransform();
    }
  }

  /**
   * Mirror the preview canvas's live CSS transform onto the stencil (Issue #1489).
   *
   * The SVG shares the canvas's coordinate space: the overlay div is positioned at
   * the canvas's top-left and sized to the canvas, and the SVG fills it at (0, 0).
   * The canvas's `transform-origin` is expressed in canvas-local pixels, so the very
   * same transform/origin pair applies unchanged and keeps the two in lockstep.
   *
   * Pass empty strings to drop the transform once real geometry has been recomputed.
   */
  setGestureTransform(transform: string, transformOrigin: string, transition: string): void {
    this.gestureTransform = transform;
    this.gestureTransformOrigin = transformOrigin;
    this.gestureTransition = transition;
    this.applyGestureTransform();
  }

  /**
   * Hide the shading picture while a camera flight paints the shading itself, and show it again at
   * landing (Issue #3127). Only the SVG is hidden, with `visibility`, so the overlay keeps its
   * click target and its CSS animations do not restart.
   */
  setFlightMode(on: boolean): void {
    this.flightMode = on;
    this.applyGestureTransform();
  }

  private applyGestureTransform(): void {
    const svg = this.spotlightSvg?.nativeElement;
    if (!svg) {
      this.gestureTargetEl = null;
      return;
    }

    svg.style.visibility = this.flightMode ? 'hidden' : '';
    svg.style.transition = this.gestureTransition;
    svg.style.transformOrigin = this.gestureTransformOrigin;
    svg.style.transform = this.gestureTransform;
    this.gestureTargetEl = svg;
  }

  /**
   * Calculate scaled box for spotlight, plus the proportional feather (Issue #1496).
   * SIMPLIFIED: Just scale from canvas pixel space to display space.
   * No offset needed - the overlay div itself is positioned at the canvas offset.
   */
  private calculateScaledBox(): void {
    if (!this.highlightBox) {
      // Clear derived geometry so a dismissed highlight cannot leave stale values
      // behind. The template is gated by `@if (highlightBox)` today, but keeping
      // state consistent avoids surprises if the stencil is ever rendered without
      // that guard, and makes the "no highlight" state unambiguous while debugging.
      this.scaledBox = { x: 0, y: 0, width: 0, height: 0 };
      this.effectiveGradientWidth = 0;
      this.calculateRegions();
      return;
    }

    this.scaledBox = this.scaleBox(this.highlightBox);

    // `scaledBox` already includes `canvasScale`, so the feather stays constant
    // *relative to the object* across zoom levels with no extra zoom plumbing. It also
    // composes with the #1489 gesture transform, which CSS-scales the whole stencil.
    this.effectiveGradientWidth = resolveFeatherPx(this.featherPercent, this.scaledBox.width, this.scaledBox.height);
    this.calculateRegions();
  }

  /**
   * Work out what to draw for each region: its window, and the label beside it (Issue #2254).
   *
   * With no `highlightRegions`, the one region is `highlightBox`, and the result is the single
   * soft-edged window this component has always drawn.
   */
  private calculateRegions(): void {
    const regions: readonly HighlightRegion[] = !this.highlightBox
      ? NO_HIGHLIGHT_REGIONS
      : this.highlightRegions.length > 0
        ? this.highlightRegions
        : [{ box: this.highlightBox }];
    const texts = regions.map(region => cleanHighlightLabel(region.label));

    // Every window is keyed by its box on the page, so a window that stays highlighted keeps its
    // element and only one that appears or disappears fades (Issue #3127).
    const previous = new Map(this.cutouts.map(cutout => [cutout.key, cutout]));
    const windows: { box: BoundingBox; state: SpotlightWindowState }[] = regions.map(region => ({
      box: region.box,
      state: 'shown',
    }));
    const dimmedKey = this.dimmedBox ? boxKey(this.dimmedBox) : null;
    if (this.dimmedBox && !windows.some(window => boxKey(window.box) === dimmedKey)) {
      windows.push({ box: this.dimmedBox, state: 'dimmed' });
    }
    const current = new Set(windows.map(window => boxKey(window.box)));
    for (const [key, cutout] of previous) {
      if (current.has(key) || this.leaving.has(key) || cutout.state === 'leaving') continue;
      if (this.fadeChanges) this.startLeaving(key, cutout.pageBox);
    }
    for (const key of current) this.stopLeaving(key);
    for (const [, leaving] of this.leaving) windows.push({ box: leaving.box, state: 'leaving' });

    this.cutouts = windows.map((window, index) => {
      const key = boxKey(window.box);
      const box = this.scaleBox(window.box);
      return {
        ...box,
        // Each window gets a feather in proportion to its own size (Issue #1496), so a small
        // region beside a large one does not drown in the large one's halo.
        feather: resolveFeatherPx(this.featherPercent, box.width, box.height),
        filterId: index === 0 ? `blur-${this.maskId}` : `blur-${this.maskId}-${index}`,
        key,
        pageBox: window.box,
        state: window.state,
        entering: window.state !== 'leaving' && this.markEntering(key, previous),
      };
    });
    this.annotated = regions.length > 1 || texts.some(text => !!text);

    // Outlines and labels belong to the highlighted regions, which come first; dimmed and fading
    // windows are drawn without them.
    const outlines = this.cutouts.slice(0, regions.length).map(cutout => outlineRect(cutout));
    const placed = placeLabels(
      outlines.map((box, index) => ({
        box,
        size: texts[index] ? { width: estimateLabelWidth(texts[index]), height: LABEL_HEIGHT_PX } : null,
      })),
      {
        gap: LABEL_GAP_PX,
        margin: LABEL_MARGIN_PX,
        bounds: { width: toFiniteDimension(this.canvasWidth), height: toFiniteDimension(this.canvasHeight) },
      }
    );
    this.labels = placed.flatMap((rect, index) => (rect ? [{ ...rect, text: texts[index] ?? '' }] : []));

    this.regionDescriptions = this.annotated
      ? texts.map((text, index) => `Region ${index + 1} of ${regions.length}: ${text ?? 'no label'}`)
      : [];
    this.ariaLabel = !this.annotated
      ? 'Object highlight'
      : regions.length === 1
        ? `Highlighted region: ${texts[0]}`
        : `${regions.length} highlighted regions`;
  }

  /**
   * Tell whether a window fades in: it appeared with a selection change while other windows were
   * on screen, or it is still within its fade. The first highlight appears with the overlay's own
   * fade, and a hover shows its window at once.
   */
  private markEntering(key: string, previous: ReadonlyMap<string, SpotlightCutout>): boolean {
    if (this.entering.has(key)) return true;
    if (!this.fadeChanges || previous.has(key) || previous.size === 0) return false;
    this.entering.set(
      key,
      setTimeout(() => this.entering.delete(key), SPOTLIGHT_FADE_IN_MS)
    );
    return true;
  }

  /** Keep a window that is no longer highlighted on screen while it fades out. */
  private startLeaving(key: string, box: BoundingBox): void {
    const timer = setTimeout(() => {
      this.leaving.delete(key);
      this.cutouts = this.cutouts.filter(cutout => cutout.key !== key);
    }, SPOTLIGHT_FADE_OUT_MS);
    this.leaving.set(key, { box, timer });
  }

  /** Drop a fading window, because it is highlighted again or the overlay is going away. */
  private stopLeaving(key: string): void {
    const leaving = this.leaving.get(key);
    if (!leaving) return;
    clearTimeout(leaving.timer);
    this.leaving.delete(key);
  }

  ngOnDestroy(): void {
    for (const key of [...this.leaving.keys()]) this.stopLeaving(key);
    for (const timer of this.entering.values()) clearTimeout(timer);
    this.entering.clear();
  }

  /**
   * Scale a box from canvas pixel space to canvas display space. Every value is coerced to a
   * finite number so a bad box or scale cannot push NaN into the SVG attributes - and, because
   * the feather is derived from these, cannot make it NaN either.
   */
  private scaleBox(box: BoundingBox): BoundingBox {
    const scale = Number.isFinite(this.canvasScale) && this.canvasScale > 0 ? this.canvasScale : 1;
    return {
      x: toFiniteDimension(box.x * scale),
      y: toFiniteDimension(box.y * scale),
      width: Math.max(0, toFiniteDimension(box.width * scale)),
      height: Math.max(0, toFiniteDimension(box.height * scale)),
    };
  }

  /**
   * Handle click to dismiss overlay.
   */
  onOverlayClick(): void {
    this.dismiss.emit();
  }

  /**
   * Handle ESC key to dismiss overlay.
   */
  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.dismissOnEscape && this.highlightBox) {
      this.dismiss.emit();
    }
  }
}

/** The outline of a region: its box, grown by the outset on every side. */
function outlineRect(box: BoundingBox): LabelRect {
  return {
    x: box.x - OUTLINE_OUTSET_PX,
    y: box.y - OUTLINE_OUTSET_PX,
    width: box.width + OUTLINE_OUTSET_PX * 2,
    height: box.height + OUTLINE_OUTSET_PX * 2,
  };
}

/**
 * How clear a window is (Issue #3127): `shown` fully, `dimmed` half (the selected region while
 * another row is hovered), and `leaving` fading out after the selection moved elsewhere.
 */
export type SpotlightWindowState = 'shown' | 'dimmed' | 'leaving';

/** One region's window in the overlay: its box on screen, its soft edge, and its blur filter. */
export interface SpotlightCutout extends BoundingBox {
  /** Width of the soft edge, in screen pixels. */
  feather: number;
  /** DOM id of the blur filter that draws the soft edge. */
  filterId: string;
  /** Identifies the window across changes: its box on the page, rounded. */
  key: string;
  /** The window's box on the page, before scaling to the screen. */
  pageBox: BoundingBox;
  state: SpotlightWindowState;
  /** True for a window that appeared with a selection change and fades in. */
  entering: boolean;
}

/** Identify a region's window by its box on the page, ignoring sub-pixel noise. */
function boxKey(box: BoundingBox): string {
  return [box.x, box.y, box.width, box.height].map(value => Math.round(value * 10) / 10).join(',');
}

/** A placed label: where it is on screen, and what it says. */
export interface SpotlightLabel extends LabelRect {
  text: string;
}

/**
 * Bounding box interface.
 */
export interface BoundingBox {
  x: number; // Left edge (pixels)
  y: number; // Top edge (pixels)
  width: number; // Width (pixels)
  height: number; // Height (pixels)
}
